import { getDataDisplayName } from "./data-display-name";
import {
  AdditionalData,
  DataNames,
  DateTypes,
  DataRanges,
  SaleType,
  Texts,
  DataSources,
  DataTypes,
  DashType,
} from "./configs";
import {
  calculateMovingAverage,
  filterDataFromXPoint,
  filterGungusGeoJson,
  filterIndexsById,
  generateInDeIndex,
  generateAptBBox,
  filterGeoJsonById,
  filterDataFromDate,
  filterGeoJsonByIds,
  getBiggestClosestData,
  filterFrontNullDataIndexes,
} from "./data-filter";
import {
  filterDataFromIndex,
  filterDates,
  filterDatesFromIndex,
  formatDate,
  ymdToLong,
} from "./date";
import {
  deepCopy,
  formatNumber,
  formatYAxisLabel,
  getAverage,
  getFilterDesc,
  getSystemColor,
  hasMoreThanHalfNull,
  roundTo,
  sortByProperty,
} from "./util";

export const getDataRequest = (
  dataType,
  dataName,
  dateType,
  dataRange,
  dataSource,
  options = {},
  funcs = {},
) => {
  return new DataRequest(
    dataType,
    dataName,
    dateType,
    dataRange,
    dataSource,
    options,
    funcs,
  );
};

export const getRequestId = (
  dataType,
  dataName,
  dateType,
  dataRange,
  dataSource,
  options = {},
  funcs = {},
) => {
  return `${dataType}${dataName}${dateType}${dataRange}${dataSource}${JSON.stringify(
    options,
  )}${JSON.stringify(funcs)}`;
};

export const getNameKey = (metadata) => {
  if (metadata.nameInfo.dataRange === DataRanges.GUNGU) {
    return `${metadata.nameInfo.sidoName}${metadata.nameInfo.name}`;
  } else if (metadata.nameInfo.dataRange === DataRanges.DONG) {
    return `${metadata.nameInfo.sidoName}${metadata.nameInfo.gunguName}${metadata.nameInfo.name}`;
  }
  return metadata.nameInfo.name;
};

export const getNameKeyByNames = (sidoName, gunguName) => {
  return `${sidoName}${gunguName}`;
};

/**
 * 지역 원본명은 데이터 매칭에 쓰고, 화면 라벨은 displayName이 있으면 우선 사용한다.
 */
const getRegionDisplayName = (region, fallbackNameInfo = null) => {
  return (
    region?.displayName ||
    region?.sidoName ||
    fallbackNameInfo?.name ||
    region?.metadata?.nameInfo?.name ||
    ""
  );
};

export class GeoJson {
  constructor(data) {
    this.data = data;
    this.zoom = 15;
  }

  updateAptsBBox = (apts, bboxRatio = 1) => {
    const bbox = generateAptBBox(apts, bboxRatio);
    if (bbox) {
      return new GeoJson({ ...this.data, bbox });
    }
    return this;
  };

  removeBBox = () => {
    return new GeoJson({ ...this.data, bbox: undefined });
  };

  hasId = (id) => {
    const filteredFeatures = this.data.features.filter(
      (feature) => feature?.properties?.metadata?.nameInfo?.id === id,
    );
    if (filteredFeatures.length === 0) {
      return false;
    }
    return true;
  };

  filterById = (id, bboxRatio = 1) => {
    const data = filterGeoJsonById(this.data, id, bboxRatio);
    return new GeoJson(data);
  };

  filterByIds = (ids, bboxRatio = 1) => {
    const data = filterGeoJsonByIds(this.data, ids, bboxRatio);
    return new GeoJson(data);
  };

  filterGungusGeoJson = (sidoName, bboxRatio = 1) => {
    const data = filterGungusGeoJson(this.data, sidoName, bboxRatio);
    return new GeoJson(data);
  };

  getMapInfo = (boundRatio = 1) => {
    let center;
    let bounds;
    const minX = this.data.bbox[0];
    const minY = this.data.bbox[1];
    const maxX = this.data.bbox[2];
    const maxY = this.data.bbox[3];

    // Calculate the center of the bbox
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    center = new window.naver.maps.LatLng(centerY, centerX);

    // Calculate the width and height of the original bounds
    const width = maxX - minX;
    const height = maxY - minY;

    // Adjust the bounds by the boundRatio
    const adjustedMinX = centerX - (width / 2) * boundRatio;
    const adjustedMaxX = centerX + (width / 2) * boundRatio;
    const adjustedMinY = centerY - (height / 2) * boundRatio;
    const adjustedMaxY = centerY + (height / 2) * boundRatio;

    // Create a LatLngBounds object with the adjusted bounds
    bounds = new window.naver.maps.LatLngBounds(
      new window.naver.maps.LatLng(adjustedMinY, adjustedMinX), // 남서쪽 좌표
      new window.naver.maps.LatLng(adjustedMaxY, adjustedMaxX), // 북동쪽 좌표
    );

    return {
      center,
      bounds,
      zoom: this.zoom,
    };
  };
}

export class DataRequest {
  constructor(
    dataType,
    dataName,
    dateType,
    dataRange,
    dataSource,
    options = {},
    funcs = {},
    displayName = "",
  ) {
    this.dataType = dataType;
    this.dataName = dataName;
    this.dateType = dateType;
    this.dataRange = dataRange;
    this.dataSource = dataSource;
    this.options = options;
    this.funcs = funcs;
    this.displayName = displayName;
  }

  getKey = () => {
    return getRequestId(
      this.dataType,
      this.dataName,
      this.dateType,
      this.dataRange,
      this.dataSource,
      this.options,
      this.funcs,
    );
  };

  setOption = (key, value) => {
    this.options = { ...this.options, [key]: value };
  };

  copy = () => {
    return new DataRequest(
      this.dataType,
      this.dataName,
      this.dateType,
      this.dataRange,
      this.dataSource,
      { ...this.options }, // Spread operator to create a shallow copy of options
      { ...this.funcs },
      this.displayName,
    );
  };
}

export class Responses {
  constructor(responses) {
    this.responses = responses;
    this.map = new Map();
    for (var response of responses) {
      this.map.set(response.getKey(), response);
    }
  }

  hasFilter = () => {
    for (const r of this.responses) {
      if (r.hasFilter()) {
        return true;
      }
    }
    return false;
  };

  getIndexs = () => {
    return this.responses.flatMap((r) => r.data);
  };

  getIndexCount = () => {
    var count = 0;
    for (const r of this.responses) {
      count += r.data ? r.data.length : 0;
    }
    return count;
  };

  getMinMax = () => {
    let plusSum = 0;
    let plusCount = 0;
    let minusSum = 0;
    let minusCount = 0;

    for (const r of this.responses) {
      for (const index of r.data) {
        for (const d of index.data) {
          if (d > 0) {
            plusSum += d;
            plusCount++;
          } else if (d < 0) {
            minusSum += d;
            minusCount++;
          }
        }
      }
    }

    // + 구간 평균, - 구간 평균
    const plusAvg = plusCount > 0 ? plusSum / plusCount : 0;
    const minusAvg = minusCount > 0 ? minusSum / minusCount : 0;

    // 절대값 중 작은 것
    const absMin = Math.max(Math.abs(plusAvg), Math.abs(minusAvg));

    // min, max 재설정 (대칭 구간)
    const min = -absMin;
    const max = absMin;
    return { min, max };
  };

  get = (index) => {
    return this.responses[index];
  };

  hasResponse = () => {
    return this.responses.length > 0;
  };

  getDateRange = () => {
    let min = Infinity;
    let max = -Infinity;
    for (const r of this.responses) {
      if (r.dates && r.dates.length > 0) {
        if (r.dates[0] < min) min = r.dates[0];
        if (r.dates[r.dates.length - 1] > max) max = r.dates[r.dates.length - 1];
      }
    }
    if (min === Infinity || max === -Infinity) return null;
    return { startDate: min, endDate: max };
  };

  /**
   * 컴포넌트를 렌더링합니다.
   * @returns {DataResponse}
   */
  getResponse = (request) => {
    return this.map.get(request.getKey());
  };

  /**
   * 컴포넌트를 렌더링합니다.
   * @returns {DataResponse}
   */
  getResponseById = (requestId) => {
    return this.map.get(requestId);
  };

  getResponseHasOption = (key, value) => {
    return this.responses.find((r) => {
      return r.getOption(key) === value;
    });
  };

  getDateType = () => {
    var dateType = DateTypes.YEARLY;
    for (var res of this.responses) {
      if (res.dateType === DateTypes.WEEKLY) {
        return DateTypes.WEEKLY;
      } else if (res.dateType === DateTypes.MONTHLY) {
        return DateTypes.MONTHLY;
      } else if (res.dateType === DateTypes.QUARTERLY) {
        return DateTypes.QUARTERLY;
      } else if (res.dateType === DateTypes.DAILY) {
        return DateTypes.DAILY;
      }
    }
    return dateType;
  };

  merge(interpolate = false) {
    // 모든 dates 합치기
    const allDates = new Set();
    for (const lake of this.responses) {
      for (const d of lake.dates) {
        allDates.add(d);
      }
    }
    const unifiedDates = Array.from(allDates).sort((a, b) => a - b);
    this.dates = unifiedDates;

    const responses = [];

    // 각 response 정렬 및 보간
    for (const lake of this.responses) {
      const originDates = lake.dates;
      const newdata = [];

      for (const dto of lake.data) {
        const dateToDataMap = new Map();
        const dtoData = dto.data;

        if (originDates && dtoData) {
          for (let i = 0; i < originDates.length; i++) {
            dateToDataMap.set(originDates[i], dtoData[i]);
          }
        }

        // 통합 날짜 기준 데이터 정렬
        const alignedData = unifiedDates.map((date) =>
          dateToDataMap.has(date) ? dateToDataMap.get(date) : null,
        );

        // interpolate = true일 경우 null 값 보간
        if (interpolate) {
          for (let i = 0; i < alignedData.length; i++) {
            if (alignedData[i] == null) {
              // 이전 유효값 찾기
              let prevIdx = i - 1;
              while (prevIdx >= 0 && alignedData[prevIdx] == null) prevIdx--;
              // 다음 유효값 찾기
              let nextIdx = i + 1;
              while (
                nextIdx < alignedData.length &&
                alignedData[nextIdx] == null
              )
                nextIdx++;

              if (prevIdx >= 0 && nextIdx < alignedData.length) {
                const prevVal = alignedData[prevIdx];
                const nextVal = alignedData[nextIdx];
                const prevDate = unifiedDates[prevIdx];
                const nextDate = unifiedDates[nextIdx];
                const currDate = unifiedDates[i];

                // 선형 보간 계산
                const ratio = (currDate - prevDate) / (nextDate - prevDate);
                alignedData[i] = prevVal + (nextVal - prevVal) * ratio;
              } else if (prevIdx >= 0 && nextIdx >= alignedData.length) {
                // trailing null: 다음 유효값이 없으면 마지막 유효값으로 채움
                alignedData[i] = alignedData[prevIdx];
              }
            }
          }
        }

        // 새로운 DataIndex 객체 생성
        const obj = dto.toObject();
        newdata.push(
          new DataIndex(
            { ...obj, data: alignedData },
            unifiedDates,
            dto.dateType,
            dto.request,
          ),
        );
      }

      responses.push(lake._gen(newdata, unifiedDates));
    }

    return responses;
  }

  /**
   * 모든 DataIndex의 "첫 유효값 날짜"를 구해서,
   * 그 중 가장 늦은 날짜(maxFirstValidDate) 이후만 각 response에서 남긴다.
   *
   * - dateType/length 달라도 OK
   * - dates를 서로 강제로 맞추지 않음 (각자의 grid 유지)
   * - 1개로 붕괴하는 문제 거의 없음
   */
  /**
   * anchorResponse가 있으면 해당 response의 첫 유효값 날짜를 cutoff으로 사용 (anchor 데이터는 전부 표시).
   * 없으면 기존 동작: 모든 series 중 가장 늦은 firstValidDate를 cutoff으로 사용.
   */
  filterFrontNull(anchorResponse = null) {
    if (!Array.isArray(this.responses) || this.responses.length === 0)
      return [];

    const isNull = (v) => v === null || v === undefined;

    let cutoff = null;

    if (anchorResponse) {
      // anchor 모드: anchor response의 첫 유효값 날짜 중 가장 이른 날짜를 cutoff으로
      const dates = anchorResponse.dates ?? [];
      for (const dto of anchorResponse.data ?? []) {
        const arr = dto.data ?? [];
        for (let i = 0; i < arr.length && i < dates.length; i++) {
          if (!isNull(arr[i])) {
            const firstDate = dates[i];
            if (firstDate != null) {
              cutoff = cutoff == null ? firstDate : Math.min(cutoff, firstDate);
            }
            break;
          }
        }
      }
    } else {
      // 기존 동작: 전체에서 "가장 늦은 firstValidDate" 찾기
      for (const lake of this.responses) {
        const dates = lake.dates ?? [];
        for (const dto of lake.data ?? []) {
          const arr = dto.data ?? [];
          let firstIdx = -1;
          for (let i = 0; i < arr.length && i < dates.length; i++) {
            if (!isNull(arr[i])) {
              firstIdx = i;
              break;
            }
          }
          if (firstIdx < 0) continue;
          const firstDate = dates[firstIdx];
          if (firstDate == null) continue;
          cutoff = cutoff == null ? firstDate : Math.max(cutoff, firstDate);
        }
      }
    }

    // cutoff를 못 구하면(전부 null) -> 빈 결과(정책)
    if (cutoff == null) {
      return this.responses.map((lake) => lake._gen([], []));
    }

    // anchor 모드: cutoff 이상(inclusive)으로 필터 → anchor 첫 데이터 포인트 포함
    // 일반 모드: cutoff 초과(exclusive) → 기존 동작 유지
    const inclusive = anchorResponse != null;
    const out = [];
    for (const lake of this.responses) {
      const { data, dates } = filterDataFromDate(lake.data, lake.dates, cutoff, inclusive);
      out.push(lake._gen(data, dates));
    }

    return out;
  }
}

export class DataIndex {
  constructor(data, dates, dateType, request) {
    const copy = JSON.parse(JSON.stringify(data)); // 깊은 복사 수행
    Object.entries(copy).forEach(([key, value]) => {
      this[key] = value; // 각 키와 값을 this (클래스 인스턴스)에 동적으로 할당합니다.
    });
    this.dates = dates;
    this.dateType = dateType;
    this.request = request;
  }

  isAllNull = () => {
    // 모든 데이터가 null인지
    if (!Array.isArray(this.data)) return true; // 혹은 false, 상황에 맞게
    return this.data.every((v) => v === null);
  };

  getLastValue = () => {
    const arr = this.data;
    if (!Array.isArray(arr) || arr.length === 0) return null;

    // 뒤에서 앞으로 탐색하면서 유효한 값 찾기
    for (let i = arr.length - 1; i >= 0; i--) {
      const v = arr[i];
      if (v !== null && v !== undefined) {
        return v;
      }
    }

    return null; // 모두 null/undefined이면 null 리턴
  };

  toObject() {
    const { ...obj } = this;
    return { ...obj };
  }

  hasData = () => {
    return this.data.length > 0 && this.dates.length > 0;
  };

  getId = () => {
    return `${this.metadata.nameInfo.id}`;
  };

  getKey = () => {
    // Filter가있 있으면 id가 동일한 것들이 있으므로
    // map key사용위해서 filter를 넣음
    return `${this.metadata.nameInfo.id}${this.getFilterDesc()}`;
  };

  getName = () => {
    return `${this.metadata.nameInfo.displayName || this.metadata.nameInfo.name}`;
  };

  getRegionNames = (regionNames = []) => {
    const nameInfo = this.metadata?.nameInfo;
    if (!nameInfo) return "";
    const { sidoName, sidoDisplayName, gunguName, dongName } = nameInfo;
    const names = [];
    if (regionNames.includes(DataRanges.SIDO) && sidoName)
      names.push(sidoDisplayName || sidoName);
    if (regionNames.includes(DataRanges.GUNGU) && gunguName)
      names.push(gunguName);
    if (regionNames.includes(DataRanges.DONG) && dongName) names.push(dongName);
    return names.join(" ");
  };

  getDisplayName = (options = {}) => {
    // backward compatibility: getDisplayName(true) or getDisplayName(false)
    if (typeof options === "boolean") {
      options = { onlyName: options };
    }

    const {
      displayOnlyName = false,
      includeRegionNames = false,
      regionNames = [],
      includeName = true,
    } = options;

    const dataInfo = this.metadata?.dataInfo;
    const nameInfo = this.metadata?.nameInfo;
    const fallbackName = nameInfo?.name ?? "";
    const displayFallbackName = nameInfo?.displayName || fallbackName;
    const displayName = displayOnlyName
      ? ""
      : dataInfo
      ? getDataDisplayName(dataInfo.dataName, dataInfo.dataType)
      : displayFallbackName;

    if (dataInfo?.dataRange?.value === DataRanges.ECONOMY) {
      return displayName;
    }

    if (includeRegionNames) {
      const regionStr = this.getRegionNames(regionNames);
      return `${displayName}(${regionStr} ${displayFallbackName})`;
    }
    if (includeName && displayFallbackName) {
      return `${displayName}(${displayFallbackName})`;
    }
    return displayName;
  };

  getSumOfData = () => {
    let value = 0;
    for (const v of this.data) {
      value += v;
    }
    return value;
  };

  getData = (key) => {
    if (this.dataMap !== undefined) {
      return this.dataMap[key];
    }
    return undefined;
  };

  getDate = (key) => {
    if (this.dateMap !== undefined) {
      return this.dateMap[key];
    }
    return undefined;
  };

  getAverage = (percent = 100, useAbs = false) => {
    const magnification = this.metadata?.dataInfo?.magnification ?? 1;
    return getAverage(this.data, percent, useAbs) * magnification;
  };

  getAveragePlotLine = ({ color = "black", width = 1, text = "평균" }) => {
    const nameInfo = this.metadata?.nameInfo;
    const dataInfo = this.metadata?.dataInfo;
    const fallbackName = [
      dataInfo?.dataType?.label,
      dataInfo?.dataName?.label,
    ]
      .filter(Boolean)
      .join("");

    return {
      value: this.getAverage(),
      color,
      width,
      dashStyle: DashType.DASH,
      id: nameInfo
        ? `${nameInfo.name}(${nameInfo.id})`
        : `${fallbackName || text}-average`,
      label: {
        text,
        color,
      },
      zIndex: 5,
    };
  };

  filterApprovedDateApts = (from, to, additaionTo = 0) => {
    const filtered = [];
    for (var apt of this.data) {
      if (
        apt.approvedDateLong &&
        apt.approvedDateLong >= from &&
        apt.approvedDateLong <= to + additaionTo
      ) {
        filtered.push(apt);
      }
    }
    return filtered;
  };

  getDateType = () => {
    return this.dateType;
  };

  getTotalProperMoveInCount = (datesCount) => {
    // pmck는 월별, 년별일 경유 하나의 월, 하나의 연 pmck이 므로 여러개의 월, 년에 대한
    // Pmck는 날짜 갯수 만큼 곱해줘야함
    var pmck = this.getAdditional(AdditionalData.PROPER_MOVEIN_COUNT);
    if (pmck) {
      return pmck * datesCount;
    }
    return undefined;
  };

  getPmck = () => {
    return this.getAdditional(AdditionalData.PROPER_MOVEIN_COUNT);
  };

  getAdditional = (key) => {
    if (!this.metadata) return undefined;
    if (this.metadata.additional !== undefined) {
      return this.metadata.additional[key];
    }
    return undefined;
  };

  getGroupNamesByGroupType = (groupType) => {
    // 권역별에 해당하는 모든 도심권 등등을 가져옴
    return this.metadata.nameInfo.groupInfos
      .filter((g) => groupType === Texts.TOTAL || g.groupName === groupType)
      .flatMap((g) => g.names);
  };

  getGunguIndexFilteredNull = () => {
    // null 이 많은건 제외
    var gunguIndexs = [];
    for (var index of this.data) {
      if (!hasMoreThanHalfNull(index.data)) {
        gunguIndexs.push(index);
      }
    }
    return gunguIndexs;
  };

  getGunguIndexsByGroupName = (groupName, filterNull = false) => {
    // 도심권, 경의권등에 해당하는 군구 인텍스를 가져옴
    // filterNull true일 경우 null이 많은건 제외
    var gunguIndexs = [];
    for (var index of this.data) {
      if (
        index.metadata.nameInfo.gunguGroupNames &&
        index.metadata.nameInfo.gunguGroupNames.includes(groupName)
      ) {
        if (filterNull && !hasMoreThanHalfNull(index.data)) {
          gunguIndexs.push(index);
        }
      }
    }
    return gunguIndexs;
  };

  getDefaultSelectedGunguNamesByGroup = (selectedGroupNames) => {
    const selectedNames = [];
    for (var index of this.data) {
      var duplicates = index.metadata.nameInfo.gunguGroupNames.filter((n) =>
        selectedGroupNames.includes(n),
      );
      if (duplicates.length > 0) {
        selectedNames.push([duplicates[0], index.metadata.nameInfo.name]);
      }
    }
    return selectedNames;
  };

  hasId = (sidoId) => {
    return this.metadata.nameInfo.id === sidoId;
  };

  hasFilter = () => {
    return !!this.request?.options?.filter;
  };

  getFilterDesc = () => {
    var f = this.request?.options?.filter;
    return f ? getFilterDesc(f) : null;
  };
}

export class DataResponse {
  constructor(
    data,
    dates,
    request,
    displayName,
    dateType,
    info = {},
    dataMap = {},
    dateMap = {},
  ) {
    /**
     * The processed data as an array of DataIndex instances.
     * @type {DataIndex[]}
     */
    // data를 새로운 Object로 등록해야지 참조 에러가 나지 않은 안그럼 DataIndex내부 this.data가 이전데이터를 참조하는 경우가 생김
    this.data = data.map((d) => new DataIndex(d, dates, dateType, request)); // [{nameInfo: , data: [], dataName} ...]
    this.info = info;
    this.dates = dates;
    this.request = request;
    this.length = data.length;
    this.displayName = displayName;
    this.dateType = dateType;
    this.dataMap = dataMap;
    this.dateMap = dateMap;
  }

  // ✅ 요청한 형태: helper로 가공 -> _gen으로 리턴
  filterFrontNull = () => {
    // this.data는 DataIndex[] 이지만, helper는 idx.data만 쓰므로 그대로 넣어도 됨
    // 다만 _gen에 넣을 data는 raw object 배열이어야 하므로 helper에서 raw로 만들어서 리턴함
    const { data, dates } = filterFrontNullDataIndexes(this.data, this.dates);
    return this._gen(data, dates);
  };

  removeAllNullIndex = () => {
    // 데이터가 모두 null인건 삭제
    this.data = this.data.filter((item) => !item.isAllNull());
  };

  hasFilter = () => {
    return !!this.request?.options?.filter;
  };

  getFilterDesc = () => {
    var f = this.request?.options?.filter;
    return f ? getFilterDesc(f) : null;
  };

  setMinMaxToIndex = (percent = 100, useAbs = false) => {
    const avr = this.getAverage(percent, useAbs);
    const round = roundTo(avr);
    for (var index of this.data) {
      index.min = -round;
      index.max = round;
    }
  };

  getMinMax = (percent = 100, useAbs = false) => {
    const avr = this.getAverage(percent, useAbs);
    const round = roundTo(avr);
    return { min: -round, max: round };
  };

  getAverage = (percent = 100, useAbs = false) => {
    // 평균의 몇퍼센트 만 계산
    var averages = [];
    for (var index of this.data) {
      var avr = index.getAverage(percent, useAbs);
      averages.push(avr);
    }
    return getAverage(averages, percent);
  };

  getOption = (key) => {
    if (this.request && this.request.options) {
      return this.request.options[key];
    }
    return undefined;
  };

  getData = (key) => {
    if (this.dataMap !== undefined) {
      return this.dataMap[key];
    }
    return undefined;
  };

  getDate = (key) => {
    if (this.dateMap !== undefined) {
      return this.dateMap[key];
    }
    return undefined;
  };

  getIndexById = (id) => {
    for (var index of this.data) {
      if (index.hasId(id)) {
        return index;
      }
    }
    return null;
  };

  _gen = (data, dates) => {
    return new DataResponse(
      data,
      dates,
      this.getRequest(),
      this.getDisplayName(),
      this.getDateType(),
      this.getDataRange(),
      this.getInfo(),
      this.dataMap,
      this.dateMap,
    );
  };

  generateInDe = (isRate = false, includeLast = true, basedAvr = false) => {
    // index, index - 1 사이의 증감 혹은 증감율(isRate=true)을 가져오는 함수
    const { data, dates } = generateInDeIndex(
      this.data,
      this.dates,
      includeLast,
      isRate,
      basedAvr,
    );
    return this._gen(data, dates);
  };

  generateMovingAvr = (windowSize = 5) => {
    const { data, dates } = calculateMovingAverage(
      this.data,
      this.dates,
      windowSize,
    );
    return this._gen(data, dates);
  };

  getRangesFromCurrentToLast = () => {
    // MoveInCount 기본 날짜 오늘 부터 이후 전체
    const current = this.findCurrentDatesIndex();
    return [current, this.dates.length - 1];
  };

  getDatesRageStrFrom = (from) => {
    const { filterDateRange } = filterDates(
      this.dates,
      from,
      undefined,
      this.dateType,
    );
    return filterDateRange;
  };

  getDatesFromIndex = (fromIndex, toIndex) => {
    // 날짜로 필터하고 불러야함
    const from = this.dates[fromIndex];
    const to = this.dates[toIndex];
    return { from, to };
  };

  getDatesRangeStrFromXpoint = (fromXpoint, toXpoint) => {
    // 날짜로 필터하고 불러야함
    var { fromIndex, toIndex } = this.getFromToIndexByXPoint(
      fromXpoint,
      toXpoint,
    );
    const from = this.dates[fromIndex];
    const to = this.dates[toIndex];
    // 둘 다 없으면 "데이터없음", 하나라도 있으면 undefined는 빈 문자열로 표시
    if (from === undefined && to === undefined) {
      return "(데이터없음)";
    }
    const fromStr = from !== undefined ? formatDate(from, this.dateType) : "";
    const toStr = to !== undefined ? formatDate(to, this.dateType) : "";
    return `(${fromStr}~${toStr})`;
  };

  getDatesRangeStrFromIndex = (fromIndex, toIndex) => {
    // 날짜로 필터하고 불러야함
    const from = this.dates[fromIndex];
    const to = this.dates[toIndex];
    return `(${formatDate(from, this.dateType)}~${formatDate(
      to,
      this.dateType,
    )})`;
  };

  findCurrentDatesIndex = () => {
    // 현재 시간보다 큰 바로 다음 index를 리턴
    const now = Date.now(); // 현재 시간의 타임스탬프 (ms 단위)

    for (let i = 0; i < this.dates.length; i++) {
      if (this.dates[i] > now) {
        return i; // 현재 시간보다 큰 첫 번째 날짜의 인덱스 반환
      }
    }

    // 모든 날짜가 현재 시간보다 작거나 같다면 -1 반환
    return -1;
  };

  getDates = () => {
    return this.dates;
  };

  getRequest = () => {
    return this.request;
  };

  getDisplayName = () => {
    return this.displayName;
  };

  get = (index) => {
    return this.data[index];
  };

  getDateType = () => {
    return this.dateType;
  };

  getInfo = () => {
    return this.info;
  };

  getDataRange = () => {
    return this.dataRange;
  };

  isGungu = () => {
    return this.dataRange === DataRanges.GUNGU;
  };

  getKey = () => {
    return getRequestId(
      this.request.dataType,
      this.request.dataName,
      this.request.dateType,
      this.request.dataRange,
      this.request.dataSource,
      this.request.options,
      this.request.funcs,
    );
  };

  addNullData = (totalCount) => {
    // real price count, deal, lease 갯수가 안맞아서 추가해줘야함
    // 둘다 동시에 보여주고 datetime slider 에서는 같은 날짜를 사용하기 때문에
    const count = totalCount - this.dates.length;
    if (count > 0) {
      for (let i = 0; i < count; i++) {
        this.dates.unshift(null);
      }
      for (var index of this.data) {
        for (let i = 0; i < count; i++) {
          index.data.unshift(null);
        }
      }
    }
  };

  getGunguNames = () => {
    if (this.dataRange === DataRanges.GUNGU) {
      var res = [];
      for (var sido of this.data) {
        for (var gungu of sido.data) {
          res.push(gungu.metadata.nameInfo);
        }
      }
      return res;
    }
    return [];
  };

  getSidoNames = () => {
    if (
      this.dataRange === DataRanges.SIDO ||
      this.dataRange === DataRanges.GUNGU
    ) {
      return this.data.map((d) => {
        const nameInfo = d.metadata.nameInfo;
        return nameInfo.displayName || nameInfo.sidoDisplayName || nameInfo.name;
      });
    }
    return [];
  };

  getSidoNameMapData = () => {
    // 시도이름 : data map
    const map = new Map();
    for (var index of this.data) {
      map.set(index.metadata.nameInfo.name, index);
    }
    return map;
  };

  getDataFromIndex = (fromIndex, toIndex, excludeFrom = false) => {
    // gungu 는 flat 후에 해당 함수를 불러야함
    const data = filterDataFromIndex(
      this.data,
      fromIndex,
      toIndex,
      excludeFrom,
    );
    const dates = filterDatesFromIndex(
      this.dates,
      fromIndex,
      toIndex,
      excludeFrom,
    );
    return { data, dates };
  };

  getFromToIndexByXPoint = (fromXpoint, toXpoint) => {
    let fromPoint = getBiggestClosestData(
      this.data,
      this.dates,
      fromXpoint,
      false,
    );
    let toPoint = getBiggestClosestData(this.data, this.dates, toXpoint, false);
    return {
      fromIndex: fromPoint.closestIndex,
      toIndex: toPoint.closestIndex,
    };
  };

  filterFromYmd = (ymd) => {
    const value = ymdToLong(ymd);
    const { data, dates } = filterDataFromDate(this.data, this.dates, value);
    return this._gen(data, dates);
  };

  filterFromXPointTo = (fromXpoint, toXpoint) => {
    const { fromIndex, toIndex } = this.getFromToIndexByXPoint(
      fromXpoint,
      toXpoint,
    );
    return this.filterDataFromIndex(fromIndex, toIndex);
  };

  filterFromXPoint = (xPoint) => {
    const { data, closestIndex, closestXPoint } = filterDataFromXPoint(
      this.data,
      this.dates,
      xPoint,
    );
    return this._gen(data, this.dates);
    // return {
    //   response: this._gen(data, this.dates),
    //   closestIndex,
    //   closestXPoint,
    // };
  };

  filterLast = () => {
    // 실거래 같이 마지막 값을 필터하고 싶은 경우.
    return this.filterDataFromIndex(0, this.dates.length - 2);
  };

  filterDataFromIndex = (fromIndex, toIndex) => {
    const filtered = this.getDataFromIndex(fromIndex, toIndex);
    return this._gen(filtered.data, filtered.dates);
  };

  filterByIds = (ids) => {
    // 이건 gungu가 flat 되지 않음
    var data = filterIndexsById(this.data, ids);
    return this._gen(data, this.dates);
  };

  /**
   * 선택 기간 안에 실제 값이 있는 인덱스만 남긴다.
   * 행정구역 개편으로 과거 구간이 null인 지역도 현재 선택 기간에 값이 있으면 표시한다.
   */
  filterIndexsWithVisibleData = (from = undefined) => {
    const { startIndex } = filterDates(
      this.dates,
      from,
      undefined,
      this.dateType,
    );
    const data = this.data.filter((index) =>
      index.data
        .slice(startIndex)
        .some((value) => value !== null && value !== undefined),
    );
    return this._gen(data, this.dates);
  };

  /**
   * 시도/군구 선택값으로 군구 데이터를 평탄화한다.
   * 구 시도는 원본명과 displayName이 다를 수 있어 둘 다 매칭한다.
   */
  filterGungus = (selectedSidos = [], selectedGungus = []) => {
    if (this.dataRange !== DataRanges.GUNGU) {
      return this;
    }

    const selectedSidoSet = new Set(selectedSidos || []);
    const selectedGunguSet = new Set(
      (selectedGungus || []).map(([sidoName, gunguName]) => `${sidoName}${gunguName}`),
    );
    const hasSidoFilter = selectedSidoSet.size > 0;
    const hasGunguFilter = selectedGunguSet.size > 0;
    const filtered = [];

    for (const sido of this.data) {
      const sidoInfo = sido.metadata?.nameInfo || {};
      const rawSidoName = sidoInfo.name;
      const displaySidoName =
        sidoInfo.displayName || sidoInfo.sidoDisplayName || rawSidoName;
      const sidoSelected =
        !hasSidoFilter ||
        selectedSidoSet.has(rawSidoName) ||
        selectedSidoSet.has(displaySidoName);

      for (const gungu of sido.data || []) {
        const gunguInfo = gungu.metadata?.nameInfo || {};
        const gunguName = gunguInfo.name;
        const gunguSelected =
          !hasGunguFilter ||
          selectedGunguSet.has(`${rawSidoName}${gunguName}`) ||
          selectedGunguSet.has(`${displaySidoName}${gunguName}`);

        if (sidoSelected && gunguSelected) {
          filtered.push(gungu);
        }
      }
    }

    return this._gen(filtered, this.dates);
  };

  /**
   * 선택 기간 안에 표시할 값이 있는 군구인지 확인한다.
   * 신규/개편 지역은 과거 날짜축이 null로 채워질 수 있으므로 전체 기간 null 비율만으로 제외하지 않는다.
   */
  hasVisibleGunguData = (index, from = undefined) => {
    const { startIndex } = filterDates(
      this.dates,
      from,
      undefined,
      this.dateType,
    );
    const visibleData = index.data.slice(startIndex);
    return visibleData.some((value) => value !== null && value !== undefined);
  };

  /**
   * 선택 기간 기준으로 화면에 표시할 만큼 데이터가 충분한 군구인지 확인한다.
   * 제주 군구 KB 지수는 2022년 11월부터 제공되므로 제주만 결측 비율에서 제외하고 실제 값 존재 여부로 판단한다.
   */
  hasEnoughGunguData = (index, from = undefined) => {
    const { startIndex } = filterDates(
      this.dates,
      from,
      undefined,
      this.dateType,
    );
    const visibleData = index.data.slice(startIndex);
    if (visibleData.length === 0) {
      return false;
    }
    const nameInfo = index.metadata?.nameInfo || {};
    const sidoName = nameInfo.sidoDisplayName || nameInfo.sidoName;
    if (["제주도", "제주특별자치도"].includes(sidoName)) {
      // 장기간 조회에서도 제주시·서귀포시의 최근 KB 지수는 숨기지 않는다.
      return visibleData.some(
        (value) => value !== null && value !== undefined,
      );
    }
    const nullCount = visibleData.filter(
      (value) => value === null || value === undefined,
    ).length;
    return nullCount < visibleData.length / 2;
  };

  /**
   * 도심권, 경의권 등 권역명에 해당하는 군구 인덱스를 가져온다.
   * filterNull이 true이면 선택 기간 안에 null이 절반 미만인 군구만 남긴다.
   */
  filterGunguIndexsByGroupName = (groupName, filterNull = false, from = undefined) => {
    var gunguIndexs = [];
    for (var index of this.data) {
      if (
        index.metadata.nameInfo.gunguGroupNames &&
        index.metadata.nameInfo.gunguGroupNames.includes(groupName)
      ) {
        if (!filterNull || this.hasEnoughGunguData(index, from)) {
          gunguIndexs.push(index);
        }
      }
    }
    return this._gen(gunguIndexs, this.dates);
  };

  /**
   * 선택 기간 안에 실제 값이 있는 군구만 남긴다.
   */
  filterGunguIndexFilteredNull = (from = undefined) => {
    var gunguIndexs = [];
    for (var index of this.data) {
      if (this.hasEnoughGunguData(index, from)) {
        gunguIndexs.push(index);
      }
    }
    return this._gen(gunguIndexs, this.dates);
  };
}

export class Sidos {
  constructor(sidos, groups) {
    this.sidos = sidos;
    this.groups = groups;
    this.sidoMap = new Map();
    this.gunguMap = new Map();
    this.dongMap = new Map();
    this.dongGunguMap = new Map();
    this.map = this._buildMaps(sidos);
    this.initialized = sidos.length > 0;
  }

  _buildMaps = (sidos) => {
    const map = new Map();
    for (const sido of sidos) {
      const gunguMap = new Map();
      map.set(sido.sidoId, {
        sidoId: sido.sidoId,
        sido,
        gunguMap,
      });
      this.sidoMap.set(sido.sidoId, sido);
      for (const gungu of sido.gungus) {
        const dongMap = new Map();
        gunguMap.set(gungu.gunguId, {
          gunguId: gungu.gunguId,
          gungu,
          dongMap,
        });
        gungu.sido = sido;
        this.gunguMap.set(gungu.gunguId, gungu);
        for (const dong of gungu.dongs) {
          dongMap.set(dong.dongId, {
            dongId: dong.dongId,
            dong,
          });
          dong.gungu = gungu;
          dong.sido = sido;
          this.dongMap.set(dong.dongId, dong);
        }
      }
    }
    return map;
  };

  // bounds: [minLng, minLat, maxLng, maxLat]
  _filterByBounds = (
    iterable,
    bounds,
    options = null,
    getLat = (o) => o?.latitude,
    getLng = (o) => o?.longitude,
  ) => {
    if (!bounds || bounds.length !== 4) return [];

    const [minLng, minLat, maxLng, maxLat] = bounds;

    const inLat = (lat) => lat >= minLat && lat <= maxLat;

    const normalizeLng = (lng) => {
      let x = lng;
      while (x > 180) x -= 360;
      while (x < -180) x += 360;
      return x;
    };

    const minL = normalizeLng(minLng);
    const maxL = normalizeLng(maxLng);

    const inLng = (lng) => {
      const l = normalizeLng(lng);
      if (minL <= maxL) return l >= minL && l <= maxL; // normal
      return l >= minL || l <= maxL; // dateline crossing
    };

    const filtered = [];
    for (const obj of iterable) {
      if (options && options.onlyParentGungu && obj.hasParent) {
        continue;
      }
      if (
        options &&
        !options.parentGungu &&
        !options.onlyParentGungu &&
        obj.isParent
      ) {
        continue;
      }
      const lat = getLat(obj);
      const lng = getLng(obj);
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      if (inLat(lat) && inLng(lng)) filtered.push(obj);
    }
    return filtered;
  };

  filterSidosByBounds = (bounds) => {
    return this._filterByBounds(this.sidoMap.values(), bounds);
  };

  filterGungusByBounds = (bounds, options = null) => {
    return this._filterByBounds(this.gunguMap.values(), bounds, options);
  };

  filterDongsByBounds = (bounds) => {
    return this._filterByBounds(this.dongMap.values(), bounds);
  };

  getGunguByDongId = (dongId) => {
    var dong = this.getDong(dongId);
    return dong.gungu;
  };

  getGunguIdByDongId = (dongId) => {
    var dong = this.getDong(dongId);
    return dong.gungu.gunguId;
  };

  getAllGroupIds = () => {
    const ids = [];
    for (const group of this.groups) {
      ids.push(group.groupId);
    }
    return ids;
  };

  getAllSidoIds = () => {
    const ids = [];
    for (const sidoObj of this.map.values()) {
      ids.push(sidoObj.sidoId);
    }
    return ids;
  };

  getAllGunguIds = () => {
    const ids = [];
    for (const sidoObj of this.map.values()) {
      for (const gunguObj of sidoObj.gunguMap.values()) {
        ids.push(gunguObj.gunguId);
      }
    }
    return ids;
  };

  getRegionName = (id, dataRange) => {
    let region = null;
    if (dataRange === DataRanges.SIDO) {
      region = this.getSido(id);
    } else if (dataRange === DataRanges.GUNGU) {
      region = this.getGungu(id);
    } else if (dataRange === DataRanges.DONG) {
      region = this.getDong(id);
    }
    // 저장된 선택 ID가 지역 개편·필터 변경으로 사라져도 화면 렌더를 중단하지 않는다.
    return region?.metadata?.nameInfo?.name ?? "";
  };

  isParent = (gunguId) => {
    var gungu = this.getGungu(gunguId);
    return gungu != null && gungu.isParent;
  };

  getChildrenIds = (gunguId) => {
    var gungu = this.getGungu(gunguId);
    return gungu.childrenIds;
  };

  hasParent = (gunguId) => {
    var gungu = this.getGungu(gunguId);
    return gungu != null && gungu.parentId != null;
  };

  getParentId = (gunguId) => {
    var gungu = this.getGungu(gunguId);
    return gungu != null ? gungu.parentId : null;
  };

  getSidos = (sidoIds) => {
    return sidoIds.map((sidoId) => this.sidoMap.get(sidoId));
  };

  getGungus = (gunguIds) => {
    return gunguIds.map((gunguId) => this.gunguMap.get(gunguId));
  };

  getDongs = (dongIds) => {
    return dongIds.map((dongId) => this.dongMap.get(dongId));
  };

  getSido = (sidoId) => {
    return this.sidoMap.get(sidoId);
  };

  /** 시도 ID가 현재 지역 캐시에 존재하는지 확인한다. */
  hasSido = (sidoId) => {
    return this.sidoMap.has(sidoId);
  };

  /** 군구 ID가 현재 지역 캐시에 존재하는지 확인한다. */
  hasGungu = (gunguId) => {
    return this.gunguMap.has(gunguId);
  };

  /** 동 ID가 현재 지역 캐시에 존재하는지 확인한다. */
  hasDong = (dongId) => {
    return this.dongMap.has(dongId);
  };

  getGungu = (gunguId) => {
    return this.gunguMap.get(gunguId);
  };

  getDong = (dongId) => {
    return this.dongMap.get(dongId);
  };

  getDongIdsOfGungu = (gunguId) => {
    const dongs = this.getDongsOfGungu(gunguId);
    return dongs.map((obj) => obj.dongId);
  };

  /**
   * 군구 트리 전체의 동 ID를 반환한다.
   * parent 군구는 직접 동과 모든 자식 군구의 동을 포함하며 중복을 제거한다.
   */
  getDongIdsOfGunguTree = (gunguId) => {
    const gungu = this.getGungu(gunguId);
    if (!gungu) return [];

    const ids = (gungu.dongs || []).map((dong) => dong.dongId);
    if (gungu.isParent) {
      for (const childId of gungu.childrenIds || []) {
        const child = this.getGungu(childId);
        if (!child) continue;
        ids.push(...(child.dongs || []).map((dong) => dong.dongId));
      }
    }
    return Array.from(new Set(ids));
  };

  getDongsOfGungu = (gunguId) => {
    var gungu = this.gunguMap.get(gunguId);
    return gungu.dongs;
  };

  getGunguIdsOfSido = (sidoId) => {
    var gungus = this.getGungusOfSido(sidoId);
    return gungus.map((obj) => obj.gunguId);
  };

  getGungusOfSido = (sidoId, onlyParent = false, includeParent = false) => {
    const gungus = [];
    var sidoObj = this.map.get(sidoId);
    // 행정구역 개편 전후 ID가 그룹 설정에 섞여 있어도 지역분석 화면이 중단되지 않게 한다.
    if (!sidoObj) {
      return gungus;
    }
    for (const gunguObj of sidoObj.gunguMap.values()) {
      var gungu = gunguObj.gungu;
      if (onlyParent && gungu.hasParent) {
        continue;
      }
      // onlyParent면 includeParent관계없이 parent는 무조건 추가해야함.
      if (!includeParent && gungu.isParent && !onlyParent) {
        continue;
      }
      gungus.push(gungu);
    }
    return gungus;
  };

  getTopXGungusOfSido = (sidoId, from, to, onlyParent = false) => {
    var gungus = this.getGungusOfSido(sidoId, onlyParent);
    const sorted1 = sortByProperty(
      gungus,
      "currentInfo.dealPricePerSpaceAvr",
      true,
      from,
      to,
    );
    return sorted1;
  };

  getTopXGunguIdsOfSido = (sidoId, from, to, onlyParent = false) => {
    var topx = this.getTopXGungusOfSido(sidoId, from, to, onlyParent);
    return topx.map((obj) => obj.gunguId);
  };

  getGungusOfSidos = (sidoIds) => {
    const gungus = [];
    for (const sidoId of sidoIds) {
      gungus.push(...this.getGungusOfSido(sidoId));
    }
    return gungus;
  };

  getGunguIdsOfSidos = (sidoIds) => {
    return sidoIds.flatMap((o) => this.getGunguIdsOfSido(o));
  };

  getGunguNames = () => {
    var res = [];
    for (var sido of this.sidos) {
      for (var gungu of sido.gungus) {
        res.push(gungu.metadata.nameInfo);
      }
    }
    return res;
  };

  getSidoNames = () => {
    return this.sidos.map((d) => d.metadata.nameInfo.name);
  };

  getTreeViewItems = (dataRanges = null, filter = null) => {
    var items = [];
    if (dataRanges != null && dataRanges.includes(DataRanges.GROUP)) {
      for (const group of this.groups) {
        const groupItem = {
          id: group.groupId,
          name: group.groupName,
          type: DataRanges.GROUP,
          subName: formatNumber(group.population, 0),
          subNameColor: "gray",
        };
        items.push(groupItem);
      }
    }

    // Sido는 없지만 Gungu, Dong은 있는 경우
    if (
      dataRanges === null ||
      dataRanges.includes(DataRanges.SIDO) ||
      dataRanges.includes(DataRanges.GUNGU) ||
      dataRanges.includes(DataRanges.DONG)
    ) {
      let sidoIndex = 0;
      for (const sido of this.sidos) {
        const sidoDisplayName = getRegionDisplayName(sido, sido.metadata?.nameInfo);
        const sidoItem = {
          id: sido.sidoId,
          name: sidoDisplayName,
          nameInfo: sido.metadata.nameInfo,
          type: DataRanges.SIDO,
          subName: formatYAxisLabel(sido.population, 0),
          subNameColor: "gray",
          selectable:
            dataRanges === null || dataRanges.includes(DataRanges.SIDO),
          children: [],
        };
        if (
          dataRanges === null ||
          dataRanges.includes(DataRanges.GUNGU) ||
          dataRanges.includes(DataRanges.DONG)
        ) {
          const gungus = [];
          var gunguIndex = 0;
          for (const gungu of sido.gungus) {
            if (filter && filter.filterParent && gungu.isParent) {
              continue;
            }
            if (filter && filter.onlyParentGungu && gungu.hasParent) {
              continue;
            }

            const gunguPopulationName = formatYAxisLabel(gungu.population, 0);
            const gunguSecondaryName = gungu.metadata.nameInfo.secondaryName;
            const gunguItem = {
              id: gungu.gunguId,
              name: gungu.metadata.nameInfo.shortName,
              nameInfo: gungu.metadata.nameInfo,
              type: DataRanges.GUNGU,
              subName: `${gunguPopulationName} ${gunguSecondaryName}`,
              // 열 보기 트리는 인구는 이름 옆에, 시도명(`경기도`·`경기도 고양시`)은 이름 아래 줄에 따로 표시한다.
              populationName: gunguPopulationName,
              secondaryName: gunguSecondaryName,
              subNameColor: getSystemColor(sidoIndex),
              selectable:
                dataRanges === null || dataRanges.includes(DataRanges.GUNGU),
              children: [],
            };
            gunguIndex += 1;
            // Dong은 가장 밑이기 때문에 selectable 필요없음
            // dataRanges에 DONG이 포함된 경우에만 동 자식 채움
            if (dataRanges === null || dataRanges.includes(DataRanges.DONG)) {
              const dongs = [];
              for (const dong of gungu.dongs) {
                const dongItem = {
                  id: dong.dongId,
                  name: dong.metadata.nameInfo.name,
                  nameInfo: dong.metadata.nameInfo,
                  type: DataRanges.DONG,
                  subName: gungu.metadata.nameInfo.shortName,
                  subNameColor: getSystemColor(gunguIndex),
                };
                dongs.push(dongItem);
              }
              gunguItem.children = dongs;
            }
            gungus.push(gunguItem);
          }
          sidoItem.children = gungus;
        }

        sidoIndex += 1;
        items.push(sidoItem);
      }
    }
    return items;
  };

  getSidoIds = (sidoNames) => {
    const ids = [];
    for (const sidoName of sidoNames) {
      if (this.map.has(sidoName)) {
        const sido = this.map.get(sidoName);
        ids.push(sido.sidoId);
      }
    }
    return ids;
  };

  getSidoId = (sidoName) => {
    if (this.map.get(sidoName)) {
      return this.map.get(sidoName).sidoId;
    }
    return undefined;
  };

  getGunguIds = (gunguNames) => {
    const ids = [];
    for (const gunguName of gunguNames) {
      const sidoName = gunguName[0];
      if (this.map.has(sidoName)) {
        const sido = this.map.get(sidoName);
        const name = gunguName[1];
        if (sido.gunguMap.has(name)) {
          const gungu = sido.gunguMap.get(name);
          ids.push(gungu.gunguId);
        }
      }
    }
    return ids;
  };

  getGunguId = (gunguNames) => {
    var sidoName = gunguNames[0];
    var gunguName = gunguNames[1];
    if (this.map.has(sidoName)) {
      if (this.map.get(sidoName).gunguMap.has(gunguName)) {
        return this.map.get(sidoName).gunguMap.get(gunguName).gunguId;
      }
    }
    return undefined;
  };

  /**
   * 지역 검색 (시도, 군구, 동)
   * @param {string} query - 검색어 (예: "경기 서현")
   * @param {number} maxResults - 최대 결과 수 (기본 50)
   * @returns {Array} 검색 결과
   */
  searchRegions = (query, maxResults = 50) => {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const keywords = query.trim().toLowerCase().split(/\s+/);
    const results = [];

    // 1. 시도 검색
    for (const sido of this.sidoMap.values()) {
      const sidoNameLower = sido.sidoName.toLowerCase();
      const matchedKeywords = [];
      let allMatch = true;

      for (const keyword of keywords) {
        if (sidoNameLower.includes(keyword)) {
          matchedKeywords.push(keyword);
        } else {
          allMatch = false;
          break;
        }
      }

      if (allMatch && matchedKeywords.length > 0) {
        results.push({
          id: sido.sidoId,
          dataRange: DataRanges.SIDO,
          latitude: sido.latitude,
          longitude: sido.longitude,
          sidoId: sido.sidoId,
          sidoName: sido.sidoName,
          population: sido.population,
          fullName: sido.sidoName,
          matchedKeywords,
          matchScore: matchedKeywords.length,
        });
      }
    }

    // 2. 군구 검색
    for (const gungu of this.gunguMap.values()) {
      if (gungu.isParent) continue;

      const sidoName = gungu.sido?.sidoName || "";
      const sidoNameLower = sidoName.toLowerCase();
      const gunguNameLower = gungu.gunguName.toLowerCase();
      const fullNameLower = `${sidoName} ${gungu.gunguName}`.toLowerCase();
      const matchedKeywords = [];
      let allMatch = true;

      for (const keyword of keywords) {
        if (fullNameLower.includes(keyword)) {
          if (
            sidoNameLower.includes(keyword) ||
            gunguNameLower.includes(keyword)
          ) {
            matchedKeywords.push(keyword);
          }
        } else {
          allMatch = false;
          break;
        }
      }

      if (allMatch && matchedKeywords.length > 0) {
        results.push({
          id: gungu.gunguId,
          dataRange: DataRanges.GUNGU,
          latitude: gungu.latitude,
          longitude: gungu.longitude,
          sidoId: gungu.sido?.sidoId,
          sidoName,
          gunguId: gungu.gunguId,
          gunguName: gungu.gunguName,
          population: gungu.population,
          fullName: `${sidoName} ${gungu.gunguName}`,
          matchedKeywords,
          matchScore: matchedKeywords.length,
        });
      }
    }

    // 3. 동 검색
    for (const dong of this.dongMap.values()) {
      const sidoName = dong.sido?.sidoName || "";
      const gunguName = dong.gungu?.gunguName || "";
      const sidoNameLower = sidoName.toLowerCase();
      const gunguNameLower = gunguName.toLowerCase();
      const dongNameLower = dong.dongName.toLowerCase();
      const fullNameLower =
        `${sidoName} ${gunguName} ${dong.dongName}`.toLowerCase();
      const matchedKeywords = [];
      let allMatch = true;

      for (const keyword of keywords) {
        if (fullNameLower.includes(keyword)) {
          if (
            sidoNameLower.includes(keyword) ||
            gunguNameLower.includes(keyword) ||
            dongNameLower.includes(keyword)
          ) {
            matchedKeywords.push(keyword);
          }
        } else {
          allMatch = false;
          break;
        }
      }

      if (allMatch && matchedKeywords.length > 0) {
        results.push({
          id: dong.dongId,
          dataRange: DataRanges.DONG,
          latitude: dong.latitude,
          longitude: dong.longitude,
          sidoId: dong.sido?.sidoId,
          sidoName,
          gunguId: dong.gungu?.gunguId,
          gunguName,
          dongId: dong.dongId,
          dongName: dong.dongName,
          fullName: `${sidoName} ${gunguName} ${dong.dongName}`,
          matchedKeywords,
          matchScore: matchedKeywords.length,
        });
      }
    }

    // 매칭 점수 내림차순, 동일 점수면 이름순 정렬
    results.sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }
      return a.fullName.localeCompare(b.fullName);
    });

    return results.slice(0, maxResults);
  };
}

export const Data = {
  AADS: getDataRequest(
    SaleType.NORMAL,
    DataNames.AA,
    DateTypes.DAILY,
    DataRanges.SIDO,
  ),
  // deal price index
  DPIWS: getDataRequest(
    SaleType.DEAL,
    DataNames.PI,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.KBSTART,
  ),
  DPIMS: getDataRequest(
    SaleType.DEAL,
    DataNames.PI,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  DPIWG: getDataRequest(
    SaleType.DEAL,
    DataNames.PI,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  DPIMG: getDataRequest(
    SaleType.DEAL,
    DataNames.PI,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // lease price index
  LPIWS: getDataRequest(
    SaleType.LEASE,
    DataNames.PI,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  LPIMS: getDataRequest(
    SaleType.LEASE,
    DataNames.PI,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LPIWG: getDataRequest(
    SaleType.LEASE,
    DataNames.PI,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  LPIMG: getDataRequest(
    SaleType.LEASE,
    DataNames.PI,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // deal price index changed rate
  DPICRWS: getDataRequest(
    SaleType.DEAL,
    DataNames.PICR,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  DPICRMS: getDataRequest(
    SaleType.DEAL,
    DataNames.PICR,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  DPICRWG: getDataRequest(
    SaleType.DEAL,
    DataNames.PICR,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  DPICRMG: getDataRequest(
    SaleType.DEAL,
    DataNames.PICR,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // lease price index changed rate
  LPICRWS: getDataRequest(
    SaleType.LEASE,
    DataNames.PICR,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  LPICRMS: getDataRequest(
    SaleType.LEASE,
    DataNames.PICR,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LPICRWG: getDataRequest(
    SaleType.LEASE,
    DataNames.PICR,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  LPICRMG: getDataRequest(
    SaleType.LEASE,
    DataNames.PICR,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // deal real price count
  DRPCWS: getDataRequest(
    SaleType.DEAL,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  DRPCMS: getDataRequest(
    SaleType.DEAL,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  DRPCWG: getDataRequest(
    SaleType.DEAL,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  DRPCMG: getDataRequest(
    SaleType.DEAL,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  // lease real price count
  LRPCWS: getDataRequest(
    SaleType.LEASE,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  LRPCMS: getDataRequest(
    SaleType.LEASE,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  LRPCWG: getDataRequest(
    SaleType.LEASE,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  LRPCMG: getDataRequest(
    SaleType.LEASE,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  // rent real price count
  RRPCWS: getDataRequest(
    SaleType.RENT,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  RRPCMS: getDataRequest(
    SaleType.RENT,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  RRPCWG: getDataRequest(
    SaleType.RENT,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  RRPCMG: getDataRequest(
    SaleType.RENT,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  // lease and rent real price count
  LRRPCWS: getDataRequest(
    SaleType.LAR,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  LRRPCMS: getDataRequest(
    SaleType.LAR,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINISTRY,
  ),
  LRRPCWG: getDataRequest(
    SaleType.LAR,
    DataNames.RPC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  LRRPCMG: getDataRequest(
    SaleType.LAR,
    DataNames.RPC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
    DataSources.MINISTRY,
  ),
  // deal sale count
  DSCWS: getDataRequest(
    SaleType.DEAL,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  DSCMS: getDataRequest(
    SaleType.DEAL,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  DSCWG: getDataRequest(
    SaleType.DEAL,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  DSCMG: getDataRequest(
    SaleType.DEAL,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // lease sale count
  LSCWS: getDataRequest(
    SaleType.LEASE,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  LSCMS: getDataRequest(
    SaleType.LEASE,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LSCWG: getDataRequest(
    SaleType.LEASE,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  LSCMG: getDataRequest(
    SaleType.LEASE,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // rent sale count
  RSCWS: getDataRequest(
    SaleType.RENT,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  RSCMS: getDataRequest(
    SaleType.RENT,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  RSCWG: getDataRequest(
    SaleType.RENT,
    DataNames.SC,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
  ),
  RSCMG: getDataRequest(
    SaleType.RENT,
    DataNames.SC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  // move in count
  MICMS: getDataRequest(
    SaleType.NORMAL,
    DataNames.MIC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  MICYS: getDataRequest(
    SaleType.NORMAL,
    DataNames.MIC,
    DateTypes.YEARLY,
    DataRanges.SIDO,
  ),
  MICMG: getDataRequest(
    SaleType.NORMAL,
    DataNames.MIC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  MICYG: getDataRequest(
    SaleType.NORMAL,
    DataNames.MIC,
    DateTypes.YEARLY,
    DataRanges.GUNGU,
  ),
  // unsold count
  UCMS: getDataRequest(
    SaleType.NORMAL,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  UCAMS: getDataRequest(
    SaleType.AFTER,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  UCTMS: getDataRequest(
    SaleType.TOTAL,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  UCMG: getDataRequest(
    SaleType.NORMAL,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  UCAMG: getDataRequest(
    SaleType.AFTER,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  UCTMG: getDataRequest(
    SaleType.TOTAL,
    DataNames.UC,
    DateTypes.MONTHLY,
    DataRanges.GUNGU,
  ),
  DADVMS: getDataRequest(
    SaleType.DEAL,
    DataNames.ADV,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  DADVWS: getDataRequest(
    SaleType.DEAL,
    DataNames.ADV,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  LSPYMS: getDataRequest(
    SaleType.LEASE,
    DataNames.SPY,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LSPYWS: getDataRequest(
    SaleType.LEASE,
    DataNames.SPY,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  DACTMS: getDataRequest(
    SaleType.DEAL,
    DataNames.ACT,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  DACTWS: getDataRequest(
    SaleType.DEAL,
    DataNames.ACT,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  LACTMS: getDataRequest(
    SaleType.DEAL,
    DataNames.ACT,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LACTWS: getDataRequest(
    SaleType.LEASE,
    DataNames.ACT,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
  ),
  DPVMS: getDataRequest(
    SaleType.DEAL,
    DataNames.PV,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LPVMS: getDataRequest(
    SaleType.LEASE,
    DataNames.PV,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LRMS: getDataRequest(
    SaleType.NORMAL,
    DataNames.LR,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
  LRMG: getDataRequest(
    SaleType.NORMAL,
    DataNames.LR,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
  ),
};

export class RequestFactory {
  constructor(sidos) {
    this.sidos = sidos;
    if (sidos) {
      this.sidoIds = sidos.getAllSidoIds();
      this.gunguIds = sidos.getAllGunguIds();
      this.groupIds = sidos.getAllGroupIds();
    }
  }

  /**
   * Returns active gungu ids plus legacy Incheon Jung-gu/Dong-gu/Seo-gu ids for market-status datasets.
   */
  getMarketStatusLegacyIncheonGunguIds = () => {
    const legacyIncheonGunguIds = ["2811000000", "2814000000", "2826000000"];
    return Array.from(new Set([...(this.gunguIds || []), ...legacyIncheonGunguIds]));
  };

  /** 시장현황 KB 지수는 현재 활성 군구 ID만 요청한다. */
  getMarketStatusKbGunguIds = () => this.gunguIds || [];

  getOption = (id, dataRange) => {
    if (dataRange === DataRanges.SIDO) {
      return { sidoId: id };
    } else if (dataRange === DataRanges.GUNGU) {
      return { gunguId: id };
    } else if (dataRange === DataRanges.DONG) {
      return { dongId: id };
    } else if (dataRange === DataRanges.GROUP) {
      return { groupId: id };
    }
  };

  get = (
    dataType,
    dataName,
    dateType,
    dataRange,
    dataSource,
    options = {},
    funcs = {},
  ) => {
    return getDataRequest(
      dataType,
      dataName,
      dateType,
      dataRange,
      dataSource,
      options,
      funcs,
    );
  };

  groupDealPriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  groupLeasePriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  sidoDealPriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeasePriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoDealPriceIndexChangeRateM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeasePriceIndexChangeRateM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  gunguDealPriceIndexChangeRateM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeasePriceIndexChangeRateM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.gunguIds },
    );
  };

  gunguDealPriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeasePriceIndexChangeRateW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX_CHANGED_RATE,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.gunguIds },
    );
  };

  groupDealPriceIndexW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  groupLeasePriceIndexW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  groupDealPriceIndexM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  groupLeasePriceIndexM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  sidoDealPriceIndexW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeasePriceIndexW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoDealPriceIndexM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeasePriceIndexM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  gunguDealPriceIndexM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.getMarketStatusKbGunguIds() },
    );
  };

  gunguLeasePriceIndexM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.getMarketStatusKbGunguIds() },
    );
  };

  gunguDealPriceIndexW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.getMarketStatusKbGunguIds() },
    );
  };

  gunguLeasePriceIndexW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_INDEX,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.KBSTART,
      { gunguIds: this.getMarketStatusKbGunguIds() },
    );
  };

  groupDealRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.MINISTRY,
      { groupIds: this.groupIds },
    );
  };

  groupLeaseRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.MINISTRY,
      { groupIds: this.groupIds },
    );
  };

  /** 국토부 사이트(MINSITE) 기준 전국 그룹 주간 매매 거래량 요청을 만든다. */
  groupDealRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.DEAL,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.GROUP,
    DataSources.MINSITE,
    { groupIds: this.groupIds },
  );

  /** 국토부 사이트(MINSITE) 기준 전국 그룹 주간 전세 거래량 요청을 만든다. */
  groupLeaseRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.LEASE,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.GROUP,
    DataSources.MINSITE,
    { groupIds: this.groupIds },
  );

  /** 국토부 사이트(MINSITE) 기준 전국 그룹 월간 매매 거래량 요청을 만든다. */
  groupDealRealPriceCountMinsiteM = () => getDataRequest(
    DataTypes.DEAL,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.MONTHLY,
    DataRanges.GROUP,
    DataSources.MINSITE,
    { groupIds: this.groupIds },
  );

  /** 국토부 사이트(MINSITE) 기준 전국 그룹 월간 전세 거래량 요청을 만든다. */
  groupLeaseRealPriceCountMinsiteM = () => getDataRequest(
    DataTypes.LEASE,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.MONTHLY,
    DataRanges.GROUP,
    DataSources.MINSITE,
    { groupIds: this.groupIds },
  );

  groupDealRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.MINISTRY,
      { groupIds: this.groupIds },
    );
  };

  groupLeaseRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.MINISTRY,
      { groupIds: this.groupIds },
    );
  };

  sidoDealRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.MINISTRY,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.MINISTRY,
      { sidoIds: this.sidoIds },
    );
  };

  /** 국토부 사이트(MINSITE) 기준 시도 주간 매매 거래량 요청을 만든다. */
  sidoDealRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.DEAL,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINSITE,
    { sidoIds: this.sidoIds },
  );

  /** 국토부 사이트(MINSITE) 기준 시도 주간 전세 거래량 요청을 만든다. */
  sidoLeaseRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.LEASE,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.SIDO,
    DataSources.MINSITE,
    { sidoIds: this.sidoIds },
  );

  /** 국토부 사이트(MINSITE) 기준 시도 월간 매매 거래량 요청을 만든다. */
  sidoDealRealPriceCountMinsiteM = () => getDataRequest(
    DataTypes.DEAL,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINSITE,
    { sidoIds: this.sidoIds },
  );

  /** 국토부 사이트(MINSITE) 기준 시도 월간 전세 거래량 요청을 만든다. */
  sidoLeaseRealPriceCountMinsiteM = () => getDataRequest(
    DataTypes.LEASE,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.MONTHLY,
    DataRanges.SIDO,
    DataSources.MINSITE,
    { sidoIds: this.sidoIds },
  );

  sidoDealRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.MINISTRY,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.MINISTRY,
      { sidoIds: this.sidoIds },
    );
  };

  gunguDealRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.MINISTRY,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeaseRealPriceCountOpenApiM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.MINISTRY,
      { gunguIds: this.gunguIds },
    );
  };

  /**
   * 국토부 사이트(MINSITE) 기준 군구 월간 매매 거래량 요청을 만든다.
   */
  gunguDealRealPriceCountMinsiteM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.MINSITE,
      { gunguIds: this.gunguIds },
    );
  };

  /**
   * 국토부 사이트(MINSITE) 기준 군구 월간 전세 거래량 요청을 만든다.
   */
  gunguLeaseRealPriceCountMinsiteM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.MINSITE,
      { gunguIds: this.gunguIds },
    );
  };

  gunguDealRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.MINISTRY,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeaseRealPriceCountOpenApiW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.REAL_PRICE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.MINISTRY,
      { gunguIds: this.gunguIds },
    );
  };

  /** 국토부 사이트(MINSITE) 기준 군구 주간 매매 거래량 요청을 만든다. */
  gunguDealRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.DEAL,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINSITE,
    { gunguIds: this.gunguIds },
  );

  /** 국토부 사이트(MINSITE) 기준 군구 주간 전세 거래량 요청을 만든다. */
  gunguLeaseRealPriceCountMinsiteW = () => getDataRequest(
    DataTypes.LEASE,
    DataNames.REAL_PRICE_COUNT,
    DateTypes.WEEKLY,
    DataRanges.GUNGU,
    DataSources.MINSITE,
    { gunguIds: this.gunguIds },
  );

  sidoDealSaleCountM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseSaleCountM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  groupDealSaleCountW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  groupLeaseSaleCountW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  groupDealSaleCountM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  groupLeaseSaleCountM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  sidoDealSaleCountW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseSaleCountW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  gunguDealSaleCountM = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeaseSaleCountM = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  gunguDealSaleCountW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeaseSaleCountW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SALE_COUNT,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  // 매물시세 (MARKET_PRICE / MP) — NAVER 매물 호가
  // 일간 원본을 백엔드 DkCollector에서 주간/월간 평균으로 집계 후 저장하므로
  // 시장현황은 다른 항목과 동일하게 주간 데이터를 사용한다
  groupDealMarketPriceW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  groupLeaseMarketPriceW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.GROUP,
      DataSources.NAVER,
      { groupIds: this.groupIds },
    );
  };

  sidoDealMarketPriceW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseMarketPriceW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  gunguDealMarketPriceW = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  gunguLeaseMarketPriceW = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.MARKET_PRICE,
      DateTypes.WEEKLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  sidoDealAdvantage = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.ADVANTAGE_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseSupply = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.SUPPLY_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoDealActivity = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.ACTIVITY_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeaseActivity = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.ACTIVITY_INDEX,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoDealPriceView = () => {
    return getDataRequest(
      DataTypes.DEAL,
      DataNames.PRICE_VIEW,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoLeasePriceView = () => {
    return getDataRequest(
      DataTypes.LEASE,
      DataNames.PRICE_VIEW,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KBSTART,
      { sidoIds: this.sidoIds },
    );
  };

  sidoNormalUnsoldCountM = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.UNSOLD_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KOSIS,
      { sidoIds: this.sidoIds },
    );
  };

  sidoAfterUnsoldCountM = () => {
    return getDataRequest(
      DataTypes.AFTER,
      DataNames.UNSOLD_COUNT,
      DateTypes.MONTHLY,
      DataRanges.SIDO,
      DataSources.KOSIS,
      { sidoIds: this.sidoIds },
    );
  };

  /** 입주물량 연간 — 시도 단위 (NAVER 소스) */
  sidoMoveInY = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.MOVE_IN_COUNT,
      DateTypes.YEARLY,
      DataRanges.SIDO,
      DataSources.NAVER,
      { sidoIds: this.sidoIds },
    );
  };

  /** 입주물량 연간 — 군구 단위 (NAVER 소스) */
  gunguMoveInY = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.MOVE_IN_COUNT,
      DateTypes.YEARLY,
      DataRanges.GUNGU,
      DataSources.NAVER,
      { gunguIds: this.gunguIds },
    );
  };

  /** 입주물량 연간 — 그룹 단위 (KB 소스) */
  groupMoveInY = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.MOVE_IN_COUNT,
      DateTypes.YEARLY,
      DataRanges.GROUP,
      DataSources.KBSTART,
      { groupIds: this.groupIds },
    );
  };

  /** 미분양 월간 — 그룹 단위 (KOSIS 소스) */
  groupNormalUnsoldCountM = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.UNSOLD_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GROUP,
      DataSources.KOSIS,
      { groupIds: this.groupIds },
    );
  };

  /** 미분양 월간 — 군구 단위 (KOSIS 소스) */
  gunguNormalUnsoldCountM = () => {
    return getDataRequest(
      DataTypes.NORMAL,
      DataNames.UNSOLD_COUNT,
      DateTypes.MONTHLY,
      DataRanges.GUNGU,
      DataSources.KOSIS,
      { gunguIds: this.getMarketStatusLegacyIncheonGunguIds() },
    );
  };
}

const generateDataIdMap = () => {
  const map = new Map();
  for (const data of Object.values(Data)) {
    const dataId = `${data.dataType}${data.dataName}`;
    if (!map.has(dataId)) {
      map.set(dataId, []);
    }
    map.get(dataId).push(data);
  }
  return map;
};

const DataIdMap = generateDataIdMap();

export const getRequest = (
  saleType,
  dataName,
  dateType,
  regionType,
  options = {},
  dataSource = undefined,
) => {
  // dataType : lease, deal, rent, normal, total...
  // dataName : PriceIndex, RealPriceCount...
  const dataId = `${saleType}${dataName}`;
  const optionKey = JSON.stringify(options);
  const candidates = DataIdMap.get(dataId) || [];
  return candidates.find((data) => {
    const sameSource = dataSource === undefined || data.dataSource === dataSource;
    return (
      data.dateType === dateType &&
      data.dataRange === regionType &&
      JSON.stringify(data.options || {}) === optionKey &&
      sameSource
    );
  });
};

export const getDataById = (dataId, dateType, regionType, options = {}) => {
  // dataType : lease, deal, rent, normal, total...
  // dataName : PriceIndex, RealPriceCount...
  const optionKey = JSON.stringify(options);
  const candidates = DataIdMap.get(dataId) || [];
  return candidates.find(
    (data) =>
      data.dateType === dateType &&
      data.dataRange === regionType &&
      JSON.stringify(data.options || {}) === optionKey,
  );
};

export const getClosestDateType = (saleType, dataName, dateType) => {
  var dataId = `${saleType}${dataName}`;
  for (var d of DataIdMap.get(dataId) || []) {
    if (d.dateType === dateType) {
      return dateType;
    }
  }
  for (var period of [DateTypes.MONTHLY, DateTypes.WEEKLY, DateTypes.YEARLY]) {
    for (var data of DataIdMap.get(dataId) || []) {
      if (data.dateType === period) {
        return period;
      }
    }
  }
  return undefined;
};
